import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import classNames from 'classnames';
import { type KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import './AccordionPanel.css';
import { resolvedConfig } from '@ipfabric/frontend-shared-assets/tailwind/tailwindConfigResolved';
import type { TransitionDuration } from '@ipfabric/frontend-shared-assets/tailwind/tailwindConfigTypes';
import { useDebounce } from '@ipfabric/ui-components/hooks/useDebounce';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';

const DEBOUNCE_DURATION = 500;

interface AccordionPanelProps {
  label: ReactNode | string;
  content: ReactNode | string;
  onToggle?: () => void;
  arrowPosition?: 'left' | 'right';
  className?: string;
  open?: boolean;
  animationCollapseDuration?: TransitionDuration;
  backgroundHoverColorDark?: boolean;
  setHeaderRefCallback?: (ref: HTMLDivElement) => void;
}

/**
 * AccordionPanel component
 * It is collapsable element intended for show more details or hide details after click on header.
 * It is designed to be used in Accordion component but It can be used separately if parent component
 * will handle toggling between open/close states.
 */
export const AccordionPanel: FC<AccordionPanelProps> = (props) => {
  const animationDuration = props.animationCollapseDuration
    ? resolvedConfig.theme.transitionDuration[props.animationCollapseDuration]
    : '0';

  const [height, setHeight] = useState(0);
  const refContent = useRef<HTMLDivElement>(null);
  const refHeader = useRef<HTMLDivElement | null>(null);
  const debounce = useDebounce(DEBOUNCE_DURATION);

  const setHeightOfContent = useStableCallback(() => {
    if (refContent.current && props.open) {
      setHeight(refContent.current.clientHeight);
    } else {
      setHeight(0);
    }
  });

  useEffect(() => {
    setHeightOfContent();

    function handleResize() {
      debounce(setHeightOfContent);
    }

    if (props.open) {
      window.addEventListener('resize', handleResize);
    } else {
      window.removeEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.open]);

  const handleClick = () => {
    props.onToggle?.();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      refHeader.current === document.activeElement
    ) {
      handleClick();
      if (event.key === ' ') {
        event.preventDefault();
      }
    }
  };

  const handleRef = (element: HTMLDivElement) => {
    props.setHeaderRefCallback?.(element);
    refHeader.current = element;
  };

  return (
    <div className={classNames('accordion-panel', props.className)}>
      <div
        ref={handleRef}
        className={classNames(
          'accordion-panel__header',
          props.arrowPosition === 'right' &&
            'accordion-panel__header--arrow-right',
          props.backgroundHoverColorDark &&
            'accordion-panel__header--hover-dark',
        )}
        onClick={handleClick}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className='accordion-panel__header-icon-box'>
          <div
            className={classNames(
              'accordion-panel__header-icon-animation-container',
              props.open &&
                'accordion-panel__header-icon-animation-container--open',
            )}
            style={{ transitionDuration: animationDuration }}
          >
            <Icon size='xs' glyph='fa-chevron-right' />
          </div>
        </div>
        <div
          className={classNames(
            'accordion-panel__header-label-box',
            typeof props.label === 'string' && 'accordion-panel__title',
          )}
        >
          {props.label}
        </div>
      </div>
      <div
        className={classNames(
          'accordion-panel__content',
          !props.open && 'accordion-panel__content--closed',
        )}
        style={{
          height: `${height}px`,
          transitionDuration: animationDuration,
        }}
      >
        <div
          ref={refContent}
          className={classNames(
            'accordion-panel__content-inner',
            typeof props.content === 'string' &&
              'accordion-panel__content-inner--only-text',
          )}
          style={{
            transitionDuration: animationDuration,
            visibility: height ? 'visible' : 'hidden',
          }}
        >
          {props.content}
        </div>
      </div>
    </div>
  );
};
